/**
 * Headless action-clock duels (Phase 59.2): bots vs bots in real time on one planet, no
 * renderer. A thin host over `Match` — the setup is a `MatchSetup` with `clock: 'action'`,
 * the bots are `createActionBot`, and the numbers come from the action events.
 */

import { shotTally, usageByTeam, type TeamUsage } from './actionUsage';
import type { GameState, GroundWeaponKind, PlayerState, UnitIntent } from '../types';
import type { ActionEvent, SimPresenter } from '../match/match';
import { createMatchState, matchFromState, type MatchSetup } from '../match/setup';
import { engagementOf } from './actionPositions';
import { personSetOf } from '../core/scaleProfile';
import type { Match } from '../match/match';
import type { DecideView, DecideDriver } from '../match/decide';
import { createActionBot, type ActionBotMode, type ActionBotOptions, type ActionKnowledge } from './actionBot';
import { actionParamsDifferByTeam, actionParamsFor } from './botParams';
import { withArchetypes } from './botArchetypes';
import { isGameOver, checkWinCondition } from '../core/gameState';
import { PHYS_DT } from '../constants';

export interface DuelSetup extends MatchSetup {
  /** A harness's look inside (the every-switch check): each tick's events, and the finished match (its input log). Not in the report. */
  inspect?: { events?: (events: readonly ActionEvent[]) => void; done?: (match: Match) => void };
  /** Seconds before the duel is called a draw. */
  timeLimit?: number;
  /** 65.6: per team, the weapon its people hold throughout (no switching; absent = the bot's own choice). */
  handWeaponByTeam?: Array<GroundWeaponKind | undefined>;
  /** WORKBOARD §13: per team, its people's knowledge feeds (default none). */
  knowledgeByTeam?: Array<ActionKnowledge | undefined>;
  /** §14 B1: the bots engage from scored spots — for everyone, or per team. */
  positions?: boolean | boolean[];
  /** §14: which bot brain — 'default' (the mode chain), 'positions' (B1), 'utility' (B2) — for everyone, or per team. Wins over `positions`. */
  brain?: BrainSpec | BrainSpec[];
  /** 64.9 (decision 115): ticks from a bot's decision to its plan applying (default ACTION_DELIBERATION_DELAY). */
  delay?: number;
  /** Record the Match's per-tick checksum every this many ticks into the report's tickTrace (ARCHITECTURE §8 move 4). */
  traceTicks?: number;
}

export interface DuelTeamStats {
  team: number;
  shots: number;
  hits: number;
  kills: number;
  damage: number;
  survivors: number;
  /** Share of each mode in the team's living bot-ticks. */
  modeShare: Record<ActionBotMode, number>;
  coverEntries: number;
}

/** §14: what the kills looked like — mean range and shooter height advantage (person-lengths), share made from cover. */
export interface EngagementSummary { kills: number; meanRange: number; meanHeightAdv: number; fromCover: number }

/** Collects kill engagements as they happen (the shooter's spot vs the victim's, at the moment of the kill). */
export function engagementCollector(match: Match): { add: (shooterId: number, victimId: number) => void; summary: () => EngagementSummary } {
  const rows: Array<{ range: number; heightAdv: number; covered: boolean }> = [];
  return {
    add(shooterId, victimId) {
      const s = match.state.players.find(p => p.id === shooterId), v = match.state.players.find(p => p.id === victimId);
      if (!s || !v) return;
      const e = engagementOf(match.state, s, v, personSetOf(match.state));
      if (e) rows.push(e);
    },
    summary() {
      const n = rows.length || 1;
      return { kills: rows.length, meanRange: rows.reduce((a, r) => a + r.range, 0) / n, meanHeightAdv: rows.reduce((a, r) => a + r.heightAdv, 0) / n, fromCover: rows.filter(r => r.covered).length / n };
    },
  };
}

export interface DuelReport {
  /** The hidden archetypes each team played (MatchSetup.archetypes on; absent off). */
  archetypes?: string[];
  seed: string;
  winnerTeam: number | null;
  /** Seconds of match time. */
  time: number;
  /** Seconds to each kill, in order. */
  killTimes: number[];
  /** Seconds to first contact (first shot fired). */
  firstShot: number | null;
  teams: DuelTeamStats[];
  /** Ticks with any unit airborne (jumps). */
  jumps: number;
  /** §14: the kills' range, height and cover. */
  engagements: EngagementSummary;
  /** Harness upkeep (121): per team, the stances, reloads, weapons and formation slots the bots used (ai/actionUsage.ts). */
  usage: Record<number, TeamUsage>;
  /** A digest of the final state, for determinism checks. */
  checksum: string;
  /** With traceTicks: the per-tick checksum every traceTicks ticks — where two runs first part. */
  tickTrace?: number[];
}

/** A digest of an action match's state (positions, health, shots) — the smoke compares page and Node by it. */
export function duelDigest(state: GameState): string {
  let h = 2166136261;
  const mix = (x: number) => { h = Math.imul(h ^ (Math.round(x * 1e6) | 0), 16777619) >>> 0; };
  for (const p of state.players) { mix(p.surfaceDir.x); mix(p.surfaceDir.y); mix(p.surfaceDir.z); mix(p.health); mix(p.shotsFired); }
  mix(state.bulletsFired ?? 0);
  return h.toString(16);
}

/**
 * The setup a duel starts from: the action clock, deathmatch, one world. 390 (the owner: "megaplanets by default"): at the
 * human scale a megaplanet with the sides a front of ten person-heights apart (on a megaplanet's own deploy they started
 * 40–110 people apart and most duels were draws); at the classic scale the large planet as before — `front` is a person
 * layout's, so nothing there sets the distance, and no classic duel fights on a megaplanet. A setup that names its world
 * keeps that world's own layout (no front unless it names one); its own front wins too.
 */
export function duelMatchSetup(setup: DuelSetup): DuelSetup {
  const mega = setup.scale === 'human' && setup.planetSize === undefined;
  return { planets: 1, planetSize: mega ? 'megaplanet' : 'large', ...(mega ? { frontHeights: 10 } : {}), mode: 'deathmatch', hazardDensity: 'none', ...setup, clock: 'action' };
}

export function runDuel(setup: DuelSetup): DuelReport {
  const built = createMatchState(duelMatchSetup(setup)).state;
  const held = (t: number) => setup.handWeaponByTeam?.[t];
  const state = setup.handWeaponByTeam ? { ...built, players: built.players.map(p => (held(p.teamIndex) && held(p.teamIndex) !== 'rifle' ? { ...p, weapon: held(p.teamIndex) } : p)) } : built;
  const match = matchFromState({ ...state, phase: 'aiming' });
  if (setup.traceTicks) { match.tickTrace = []; match.traceEvery = setup.traceTicks; }
  // Per team when the brain is a list (a head-to-head of brains); one driver otherwise
  const specs = brainSpecs(setup);
  // the setup's bot params, with the teams' hidden archetypes under them when it has them (setup drew them; botArchetypes.ts)
  const botParams = withArchetypes(setup.botParams, match.state.botArchetypes);
  const botFor = (spec: BrainSpec, team = 0) => createActionBot(setup.seed, { ...(botParams ? { params: actionParamsFor(botParams, team) } : {}), knowledgeFor: t => setup.knowledgeByTeam?.[t], ...(setup.sharedSight ? { shareSight: true } : {}), ...(setup.delay !== undefined ? { delay: setup.delay } : {}), ...brainOptions(spec) });
  const paramsByTeam = actionParamsDifferByTeam(botParams);   // 62.48: teams with their own action knobs get their own bots
  const perTeam = Array.isArray(specs) || paramsByTeam;
  const specList = Array.isArray(specs) ? specs : [specs];
  const bot = botFor(Array.isArray(specs) ? 'default' : specs);
  const bots = perTeam ? Array.from({ length: Math.max(specList.length, paramsByTeam ? setup.teams ?? 2 : 1) }, (_, t) => botFor(specList[t % specList.length], t)) : null;
  const engagements = engagementCollector(match);
  const drive = bots ? (m: DecideView, u: PlayerState) => bots[u.teamIndex % bots.length].drive(m, u) : bot.drive;
  // 65.6: a team that holds a weapon never switches
  match.actionDriver = setup.handWeaponByTeam ? (m, u) => { const i = drive(m, u); return held(u.teamIndex) ? { ...i, weapon: undefined } : i; } : drive;

  const teams = new Map<number, DuelTeamStats>();
  const teamOf = (id: number) => match.state.players.find(p => p.id === id)!.teamIndex;
  for (const p of match.state.players) {
    if (!teams.has(p.teamIndex)) teams.set(p.teamIndex, { team: p.teamIndex, shots: 0, hits: 0, kills: 0, damage: 0, survivors: 0, modeShare: { hunt: 0, search: 0, fight: 0, cover: 0 }, coverEntries: 0 });
  }
  const killTimes: number[] = [];
  let firstShot: number | null = null;
  let over = false;
  let jumps = 0;
  const tally = shotTally();
  const onEvents = (events: ActionEvent[], gameOver: boolean) => {
    const t = match.actionTick * PHYS_DT;
    for (const e of events) {
      if (e.kind === 'fired') { teams.get(teamOf(e.unitId))!.shots++; tally.fired(match.state, e.unitId); if (firstShot === null) firstShot = t; }
      else if (e.kind === 'hitUnit') {
        const ts = teams.get(teamOf(e.bullet.ownerId))!;
        ts.hits++; ts.damage += e.damage;
        if (e.killed) { ts.kills++; killTimes.push(t); engagements.add(e.bullet.ownerId, e.targetId); }
      } else if (e.kind === 'blast' || e.kind === 'shellImpact') {
        // S7 / 64.11: kills by a rocket's burst or a seat's shell
        const owner = e.kind === 'blast' ? e.ownerId : e.shell.ownerId;
        for (const v of e.kind === 'blast' ? e.kills : e.resolution.kills) { teams.get(teamOf(owner))!.kills++; killTimes.push(t); engagements.add(owner, v); }
      } else if (e.kind === 'jumped') jumps++;
    }
    setup.inspect?.events?.(events);
    if (gameOver) over = true;
  };
  const presenter: SimPresenter = {
    holdFlightEnd: () => false, flightEndReached: () => {}, launched: () => {}, probeScan: () => {},
    geyserDeflect: () => {}, impacted: () => {}, lost: () => {}, flightOver: () => {}, airborne: () => {},
    actionEvents: onEvents,
  };

  const limitTicks = Math.round((setup.timeLimit ?? 120) / PHYS_DT);
  match.runTicks(limitTicks - match.actionTick, presenter, () => over);

  for (const [id, st] of (bots ? bots.flatMap(b => [...b.stats]) : [...bot.stats])) {
    const ts = teams.get(teamOf(id))!;
    for (const k of Object.keys(st.ticks) as ActionBotMode[]) ts.modeShare[k] += st.ticks[k];
    ts.coverEntries += st.coverEntries;
  }
  for (const ts of teams.values()) {
    const total = Object.values(ts.modeShare).reduce((a, b) => a + b, 0) || 1;
    for (const k of Object.keys(ts.modeShare) as ActionBotMode[]) ts.modeShare[k] = ts.modeShare[k] / total;
    ts.survivors = match.state.players.filter(p => p.alive && p.teamIndex === ts.team).length;
  }
  setup.inspect?.done?.(match);
  const done = isGameOver(match.state);
  return {
    seed: setup.seed,
    ...(match.state.botArchetypes ? { archetypes: match.state.botArchetypes } : {}),
    winnerTeam: done ? (checkWinCondition(match.state)?.winningTeamIndex ?? null) : null,
    time: match.actionTick * PHYS_DT,
    killTimes, firstShot,
    teams: [...teams.values()].sort((a, b) => a.team - b.team),
    jumps,
    engagements: engagements.summary(),
    usage: usageByTeam(bots ? bots.flatMap(b => [...b.stats]) : [...bot.stats], match.state, tally),
    checksum: duelDigest(match.state),
    ...(match.tickTrace ? { tickTrace: match.tickTrace } : {}),
  };
}

/** §14: the bot brains a harness can field. */
export type BrainSpec = 'default' | 'positions' | 'utility' | 'roles';

/** A setup's brain(s): `brain` if given, else B1's `positions` flag, else the default. */
export function brainSpecs(setup: { brain?: BrainSpec | BrainSpec[]; positions?: boolean | boolean[] }): BrainSpec | BrainSpec[] {
  if (setup.brain !== undefined) return setup.brain;
  if (Array.isArray(setup.positions)) return setup.positions.map(on => (on ? 'positions' : 'default') as BrainSpec);
  return setup.positions ? 'positions' : 'default';
}

/**
 * The createActionBot options the game page builds from its setup (main.ts) — the brain switch, the knowledge feeds,
 * the shared picture, the skirmish's holding and seats switches — in one place, so a test reaches the page's bots.
 */
export function hostActionBotOptions(setup: DuelSetup & { holding?: boolean; seatsUnderThreat?: boolean }): ActionBotOptions {
  const spec = setup.brain;
  const know = setup.knowledgeByTeam;
  return {
    ...brainOptions(Array.isArray(spec) ? spec[0] : spec ?? 'default'),
    ...(know ? { knowledgeFor: (t: number) => know[t] } : {}),
    ...(setup.sharedSight ? { shareSight: true } : {}),
    ...(setup.holding ? { holdingGround: true } : {}),
    ...(setup.seatsUnderThreat ? { seatsUnderThreat: true } : {}),
  };
}

/**
 * 67.24: the page's bot driver — one brain for all, or (a per-team `brain` whose sides differ) each team its own, as
 * runSkirmish drives them, so the page and the harness play the same match.
 */
export function hostActionDriver(setup: DuelSetup & { holding?: boolean; seatsUnderThreat?: boolean }): DecideDriver {
  const spec = setup.brain;
  if (!Array.isArray(spec) || spec.every(s => s === spec[0])) return createActionBot(setup.seed, hostActionBotOptions(setup)).drive;
  const drivers = spec.map(s => createActionBot(setup.seed, hostActionBotOptions({ ...setup, brain: s })));
  // (68.32: the held units' eyes ride on the driver — each side's own, as its brain is; a bare wrapper had dropped them)
  const drive: DecideDriver = (m, u) => drivers[u.teamIndex % drivers.length].drive(m, u);
  return Object.assign(drive, { observe: (m: DecideView, u: PlayerState) => (drivers[u.teamIndex % drivers.length].drive as DecideDriver).observe?.(m, u) });
}

/** The createActionBot options for a brain. */
export function brainOptions(spec: BrainSpec): { positions?: true; brain?: 'utility' | 'roles' } {
  return spec === 'positions' ? { positions: true } : spec === 'utility' ? { brain: 'utility' } : spec === 'roles' ? { brain: 'roles' } : {};
}
